import { Stack } from '@mui/material';
import { DefinedUseQueryResult } from '@tanstack/react-query';
import { every, isEmpty, some } from 'lodash';
import { useMemo } from 'react';

// Components
import LoadingSkeleton from 'src/components/common/LoadingSkeleton';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import { StyledWhiteBox } from 'src/features/CompanyDetail/config';
import SocialMediaAI from 'src/features/CompanyDetail/SocialMediaByAI/SocialMediaAI';
import SocialMediaDB from 'src/features/CompanyDetail/SocialMediaByAI/SocialMediaDB';
import { CompanySocialMediaAI } from 'src/model/company';

// Utilities
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';

const SocialMediaByAITab = ({ socialMedia }: { socialMedia: DefinedUseQueryResult<CompanySocialMediaAI, Error> }) => {
  const { data, isFetching } = socialMedia;
  const listSocialMediaDB = data?.data?.db_socials;
  const listSocialMediaAI = getArrayDefault<Array<ISocialMediaAIItem>>(data?.data?.ai_socials);

  const socialMediasList = useMemo(() => {
    return getArrayDefault(listSocialMediaDB?.social_medias).filter((item) => !isEmpty(item?.url));
  }, [listSocialMediaDB?.social_medias]);

  const chatsList = useMemo(() => {
    return getArrayDefault(listSocialMediaDB?.chats).filter((item) => !isEmpty(item?.url));
  }, [listSocialMediaDB?.chats]);

  const directoriesList = useMemo(() => {
    return getArrayDefault(listSocialMediaDB?.directories).filter((item) => !isEmpty(item?.url));
  }, [listSocialMediaDB?.directories]);

  const suggestedByAIList = useMemo(() => {
    return listSocialMediaAI.filter(
      (item) =>
        (item?.confidence || 0) > 0 &&
        every([item.status, item.url, item.display_name], (value) => value !== 'NOT_FOUND'),
    );
  }, [listSocialMediaAI]);

  const isEmptyData = [socialMediasList, chatsList, directoriesList, suggestedByAIList].every((item) => isEmpty(item));

  if (isFetching) {
    return (
      <StyledWhiteBox>
        <LoadingSkeleton count={5} height={50} />
      </StyledWhiteBox>
    );
  }

  if (isEmptyData) {
    return <CompanyInfoNoData title="No Social Media Found" content={<>This company does not have Social Media.</>} />;
  }

  return (
    <Stack gap={3} direction="column">
      {!isEmpty(socialMediasList) && (
        <StyledWhiteBox>
          <SocialMediaDB data={socialMediasList} title="Social Media" />
        </StyledWhiteBox>
      )}
      {!isEmpty(chatsList) && (
        <StyledWhiteBox>
          <SocialMediaDB data={chatsList} title="Chat" />
        </StyledWhiteBox>
      )}
      {!isEmpty(directoriesList) && (
        <StyledWhiteBox>
          <SocialMediaDB data={directoriesList} title="Directory" />
        </StyledWhiteBox>
      )}
      {!isEmpty(suggestedByAIList) && (
        <StyledWhiteBox>
          <SocialMediaAI listSocial={suggestedByAIList} title="Suggested By AI" />
        </StyledWhiteBox>
      )}
    </Stack>
  );
};

export default SocialMediaByAITab;
